import React from 'react';
import { DitherCanvas, DitherFields } from './Dither.jsx';

const SAMPLE_PROMPTS = [
  "B2B sales pipeline for an EU SaaS company",
  "Product launch with engineering and marketing",
  "Customer onboarding with auto-reminders",
  "Hiring pipeline for senior engineers",
];

const FALLBACK_BOARD = {
  name: "Sales Pipeline — EU",
  columns: [
    {
      title: "Prospects",
      cards: [
        { title: "Acme GmbH — enterprise tier", tag: "outbound", priority: "med" },
        { title: "Northwind Bank — discovery call", tag: "inbound", priority: "high" },
      ],
    },
    {
      title: "Discovery",
      cards: [
        { title: "Hanseatic Logistics — demo Tue", tag: "demo", priority: "med" },
      ],
    },
    {
      title: "Proposal",
      cards: [
        { title: "Lyra AB — €48k ACV", tag: "deal", priority: "high" },
        { title: "Zentrum AG — pricing v3", tag: "deal", priority: "med" },
      ],
    },
    {
      title: "Closed",
      cards: [
        { title: "Stockholms Bryggeri", tag: "won", priority: "low" },
      ],
    },
  ],
  automations: [
    "When card enters Discovery → create follow-up task in 3 days",
    "When card moves to Proposal → notify @sales-leads",
    "Every Monday → AI summarizes deals at risk",
  ],
};

function priorityDot(p) {
  const map = { high: '#D92626', med: '#C78A05', low: '#737373' };
  return (
    <span style={{
      display: 'inline-block',
      width: 6, height: 6,
      borderRadius: '50%',
      background: map[p] || '#737373',
      marginRight: 6,
    }} />
  );
}

function tagPill(t) {
  return (
    <span style={{
      fontSize: 10,
      fontFamily: 'JetBrains Mono, monospace',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '2px 6px',
      background: '#0A0A0A',
      color: '#FFE44D',
      borderRadius: 3,
    }}>{t}</span>
  );
}

export function AIBuilder() {
  const [stage, setStage] = React.useState('idle');
  const [prompt, setPrompt] = React.useState('');
  const [board, setBoard] = React.useState(null);
  const [showAutomations, setShowAutomations] = React.useState(false);
  const [draggedCard, setDraggedCard] = React.useState(null);
  const [thinkingMsg, setThinkingMsg] = React.useState('Reading intent…');

  React.useEffect(() => {
    if (stage !== 'thinking') return;
    const msgs = [
      'Reading intent…',
      'Drafting columns…',
      'Generating sample work…',
      'Wiring automations…',
    ];
    let i = 0;
    setThinkingMsg(msgs[0]);
    const id = setInterval(() => {
      i = (i + 1) % msgs.length;
      setThinkingMsg(msgs[i]);
    }, 1100);
    return () => clearInterval(id);
  }, [stage]);

  async function buildBoard(text) {
    setStage('thinking');
    setBoard(null);
    try {
      if (window.claude && typeof window.claude.complete === 'function') {
        const resp = await window.claude.complete({
          messages: [{
            role: 'user',
            content: `You generate kanban board specifications. Output ONLY valid JSON, no prose, no code fences.\n\nUser intent: "${text}"\n\nSchema:\n{\n  "name": "Short board name (max 6 words)",\n  "columns": [\n    {\n      "title": "Column name",\n      "cards": [\n        { "title": "Concrete realistic task", "tag": "short-tag", "priority": "high|med|low" }\n      ]\n    }\n  ],\n  "automations": [\n    "When X happens → do Y (concrete and specific)"\n  ]\n}\n\nRules:\n- 4 columns, each with 1-3 cards\n- Card titles must be specific and realistic, not generic. Include real-sounding company names, numbers, dates if appropriate.\n- Use European/EU context where it fits naturally.\n- Tags are 1-2 words, lowercase, no spaces (use hyphen).\n- 3 automations, written as "When X → Y" rules.\n- Return ONLY the JSON object.`,
          }],
        });
        const cleaned = resp.trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
        const data = JSON.parse(cleaned);
        setTimeout(() => {
          setBoard(data);
          setStage('board');
        }, 800);
        return;
      }
      throw new Error('no-llm');
    } catch (_) {
      setTimeout(() => {
        setBoard({ ...FALLBACK_BOARD, name: text.slice(0, 60) || FALLBACK_BOARD.name });
        setStage('board');
      }, 1000);
    }
  }

  function handleSubmit(e) {
    e?.preventDefault?.();
    const text = prompt.trim();
    if (!text || stage === 'thinking') return;
    buildBoard(text);
  }

  function handleDragStart(colIdx, cardIdx) {
    setDraggedCard({ colIdx, cardIdx });
  }
  function handleDrop(targetColIdx) {
    if (!draggedCard) return;
    if (draggedCard.colIdx === targetColIdx) { setDraggedCard(null); return; }
    setBoard(prev => {
      const cols = prev.columns.map(c => ({ ...c, cards: [...c.cards] }));
      const [card] = cols[draggedCard.colIdx].cards.splice(draggedCard.cardIdx, 1);
      cols[targetColIdx].cards.push(card);
      return { ...prev, columns: cols };
    });
    setDraggedCard(null);
  }

  return (
    <div className="ai-builder">
      <form onSubmit={handleSubmit} className="ai-prompt">
        <div className="ai-prompt__icon" aria-hidden>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe your work — a sales pipeline, a sprint, a hiring funnel…"
          disabled={stage === 'thinking'}
          autoComplete="off"
          spellCheck="false"
        />
        <button type="submit" disabled={stage === 'thinking' || !prompt.trim()}>
          {stage === 'thinking' ? 'Building…' : 'Build my board →'}
        </button>
      </form>

      {stage === 'idle' && (
        <div className="ai-samples">
          <span className="ai-samples__label">try:</span>
          {SAMPLE_PROMPTS.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => { setPrompt(s); buildBoard(s); }}
              className="ai-sample"
            >{s}</button>
          ))}
        </div>
      )}

      {stage === 'thinking' && (
        <div className="ai-thinking">
          <div className="ai-thinking__dither">
            <DitherCanvas field={DitherFields.thinking} pixelSize={2} fps={20} fg="#0A0A0A" />
          </div>
          <div className="ai-thinking__msg">
            <span className="ai-thinking__cursor" />
            {thinkingMsg}
          </div>
        </div>
      )}

      {stage === 'board' && board && (
        <div className="ai-result">
          <div className="ai-result__header">
            <div className="ai-result__title">
              <span className="ai-result__chip">BOARD</span>
              <h3>{board.name}</h3>
            </div>
            <div className="ai-result__actions">
              <button
                type="button"
                onClick={() => setShowAutomations(s => !s)}
                className={`ai-tab ${showAutomations ? 'is-active' : ''}`}
              >
                {showAutomations ? 'Show board' : `Show ${board.automations.length} automations`}
              </button>
              <button
                type="button"
                onClick={() => { setStage('idle'); setPrompt(''); setBoard(null); }}
                className="ai-tab"
              >New board</button>
            </div>
          </div>

          {!showAutomations && (
            <div className="ai-kanban">
              {board.columns.map((col, ci) => (
                <div
                  key={ci}
                  className="ai-col"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(ci)}
                >
                  <div className="ai-col__head">
                    <span>{col.title}</span>
                    <span className="ai-col__count">{col.cards.length}</span>
                  </div>
                  <div className="ai-col__cards">
                    {col.cards.map((card, idx) => (
                      <div
                        key={idx}
                        className="ai-card"
                        draggable
                        onDragStart={() => handleDragStart(ci, idx)}
                        style={{ animationDelay: `${(ci * 100) + idx * 60}ms` }}
                      >
                        <div className="ai-card__title">
                          {priorityDot(card.priority)}
                          {card.title}
                        </div>
                        <div className="ai-card__meta">
                          {tagPill(card.tag)}
                        </div>
                      </div>
                    ))}
                    <button type="button" className="ai-card-add">+ add</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAutomations && (
            <div className="ai-automations">
              {board.automations.map((a, i) => (
                <div key={i} className="ai-auto" style={{ animationDelay: `${i * 80}ms` }}>
                  <span className="ai-auto__num">0{i+1}</span>
                  <code>{a}</code>
                  <span className="ai-auto__status">live</span>
                </div>
              ))}
              <div className="ai-auto-add">
                + describe another rule…
              </div>
            </div>
          )}

          <div className="ai-result__footer">
            <span>↕ drag cards between columns</span>
            <span className="ai-result__footer-cta">Continue in workspace →</span>
          </div>
        </div>
      )}
    </div>
  );
}
